import type { PointAnnotation } from 'features/timeSeries/components/pointTool/usePointAnnotationTool'
import type { Ruler } from 'features/timeSeries/components/rulerTool/useRulerTool'
import {
  ChartInteractionMode,
  DataProcessingConfig,
  DataProcessingConfigSchema,
  DateConfig,
  PointAnnotationConfigSchema,
  RulerConfigSchema,
  SourceConfig,
  SourceConfigSchema,
  YAxisMode,
} from 'types'
import { z } from 'zod'

import { serializeDataProcessingConfigs } from './dataProcessingSerialization'

export const serializeSourceConfigs = (
  src: SourceConfig[]
): z.input<typeof SourceConfigSchema>[] =>
  src.map((s) => ({
    type: s.type,
    source_id: s.sourceId,
    color: s.color,
    hidden: s.hidden ?? null,
    y_axis_range: s.yAxisRange ?? null,
    // Sentinel-only fields, written only on sentinel configs so sensor and
    // derivedValue entries keep their pre-sentinel wire shape byte-for-byte.
    ...(s.type === 'sentinel' && {
      sentinel_target: s.sentinelTarget ?? null,
      sentinel_name: s.sentinelName ?? null,
      sentinel_method: s.sentinelMethod ?? null,
    }),
  }))

/**
 * Live-vs-saved equality for source configs, order-insensitive. Used by
 * `useChartSave` for dirty tracking; lives here (not in the hook) so the
 * round-trip tests can pin the contract — a freshly loaded chart compares
 * clean, a changed sentinel method/visibility compares dirty — without
 * importing the hook's heavy provider graph.
 */
export const compareSourceConfigs = (
  a: SourceConfig[],
  b: SourceConfig[]
): boolean => {
  if (a.length !== b.length) return false

  // Sort both arrays by sourceId for consistent comparison
  const sortedA = [...a].sort((x, y) => x.sourceId.localeCompare(y.sourceId))
  const sortedB = [...b].sort((x, y) => x.sourceId.localeCompare(y.sourceId))

  return sortedA.every((configA, index) => {
    const configB = sortedB[index]
    return (
      configA.sourceId === configB.sourceId &&
      configA.type === configB.type &&
      configA.color === configB.color &&
      configA.hidden === configB.hidden &&
      JSON.stringify(configA.yAxisRange) ===
        JSON.stringify(configB.yAxisRange) &&
      // Sentinel fields, `?? null`-normalized: live state carries `undefined`
      // for an unset method/name while the parsed saved chart omits the key,
      // and neither is a real difference.
      (configA.sentinelTarget ?? null) === (configB.sentinelTarget ?? null) &&
      (configA.sentinelName ?? null) === (configB.sentinelName ?? null) &&
      (configA.sentinelMethod ?? null) === (configB.sentinelMethod ?? null)
    )
  })
}

/**
 * Serialize live `Ruler` state into the snake_case form the chart entry stores.
 *
 * This is the single place that drops `startPixelOnOverlay`/`endPixelOnOverlay`.
 * Those are viewport-derived — a ruler saved at one window size would restore to
 * a visually wrong span at another — and `RulerOverlay` recomputes them from
 * `startXValue`/`endXValue` via `valToPos` on every render anyway
 * (RulerOverlay.tsx:360-374). Persist the domain values; let the overlay derive
 * the geometry.
 */
export const serializeRulerConfigs = (
  rulers: Ruler[]
): z.input<typeof RulerConfigSchema>[] =>
  rulers.map((ruler) => ({
    id: ruler.id,
    start_x_value: ruler.startXValue,
    end_x_value: ruler.endXValue,
    selected_sensor_ids: ruler.selectedSensorIds,
    is_minimized: ruler.isMinimized,
    panel_offset_y: ruler.panelOffsetY,
  }))

/** Serialize live `PointAnnotation` state into its stored snake_case form. */
export const serializePointAnnotationConfigs = (
  annotations: PointAnnotation[]
): z.input<typeof PointAnnotationConfigSchema>[] =>
  annotations.map((annotation) => ({
    id: annotation.id,
    anchor_timestamp: annotation.anchorTimestamp,
    anchor_y_ratio: annotation.anchorYRatio,
    sensor_id: annotation.sensorId,
  }))

type SerializedDateConfig = {
  mode: 'absolute' | 'relative'
  start_date?: string | null
  end_date?: string | null
  start_date_lookback?: { value: number; unit: string } | null
  end_date_lookback?: { value: number; unit: string } | null
  update_cadence_seconds?: number | null
}

export const serializeDateConfig = (dc: DateConfig): SerializedDateConfig => {
  if (dc.mode === 'absolute') {
    return {
      mode: 'absolute' as const,
      start_date: dc.startDate.toISOString(),
      end_date: dc.endDate.toISOString(),
      start_date_lookback: null,
      end_date_lookback: null,
      update_cadence_seconds: null,
    }
  }
  return {
    mode: 'relative' as const,
    start_date_lookback: dc.startDateLookback,
    end_date_lookback: dc.endDateLookback ?? null,
    update_cadence_seconds: dc.updateCadenceSeconds,
    start_date: null,
    end_date: null,
  }
}

/** The camelCase live state a save reads from. */
export interface ChartPatchState {
  name?: string
  description?: string
  sourceConfigs: SourceConfig[]
  yAxisMode: YAxisMode
  dateConfig: DateConfig
  /**
   * Omitted (not empty) when the caller does not track this state, so an
   * existing chart's configs are not clobbered by a caller that cannot see them.
   */
  dataProcessingConfigs?: DataProcessingConfig[]
  /** Ruler tool state. Omitted (not empty) by callers that do not track it. */
  rulers?: Ruler[]
  /** Pen/point annotations. Omitted (not empty) by callers that do not track it. */
  pointAnnotations?: PointAnnotation[]
  /** Which tool is open. Omitted by callers that do not track it. */
  interactionMode?: ChartInteractionMode
}

/**
 * A `type` alias, not an `interface`: the rolodex patch payload is typed as an
 * index-signature record, and TypeScript only grants implicit index signatures
 * to type aliases. An interface here fails to assign at the `rolodexPatch` call.
 */
export type ChartPatchData = {
  name: string | null
  description: string | null
  source_configs: z.input<typeof SourceConfigSchema>[]
  y_axis_mode: YAxisMode
  date_config: SerializedDateConfig
  data_processing_configs?: z.input<typeof DataProcessingConfigSchema>[]
  ruler_configs?: z.input<typeof RulerConfigSchema>[]
  point_annotation_configs?: z.input<typeof PointAnnotationConfigSchema>[]
  interaction_mode?: ChartInteractionMode
}

/**
 * Build the snake_case `data` payload a chart patch sends.
 *
 * Extracted from `useChartSave` so the round-trip tests can parse the *real*
 * payload back through `StandaloneChartSchema` rather than a hand-written
 * replica. The schema is `.transform()`-based and strips unknown keys, so a
 * payload that drifts to camelCase fails validation on the next read and the
 * state silently disappears — a replica test cannot see that drift.
 */
export const buildChartPatchData = ({
  name,
  description,
  sourceConfigs,
  yAxisMode,
  dateConfig,
  dataProcessingConfigs,
  rulers,
  pointAnnotations,
  interactionMode,
}: ChartPatchState): ChartPatchData => {
  const trimmedName = name?.trim() ?? ''
  const trimmedDescription = description?.trim() ?? ''

  return {
    name: trimmedName || null,
    description: trimmedDescription || null,
    source_configs: serializeSourceConfigs(sourceConfigs),
    y_axis_mode: yAxisMode,
    date_config: serializeDateConfig(dateConfig),
    ...(dataProcessingConfigs && {
      data_processing_configs: serializeDataProcessingConfigs(
        dataProcessingConfigs
      ),
    }),
    ...(rulers && { ruler_configs: serializeRulerConfigs(rulers) }),
    ...(pointAnnotations && {
      point_annotation_configs:
        serializePointAnnotationConfigs(pointAnnotations),
    }),
    ...(interactionMode && { interaction_mode: interactionMode }),
  }
}

/**
 * Drop any inverted y-axis range (min > max) from a raw stored chart payload.
 *
 * `YAxisRangeSchema` refines `max >= min`, so ONE bad pair — written by a
 * version that did not validate at commit time — would otherwise fail the
 * whole `StandaloneChartSchema` parse and take every other piece of stored
 * state down with it (a dropped local-state draft, or a saved chart that can
 * never load again). Sanitizing only the offending range keeps the failure
 * proportional to the corruption. Returns the input unchanged (same identity)
 * when nothing needs fixing; never throws on unexpected shapes — the real
 * schema validation still runs after this.
 */
export const sanitizeInvertedYAxisRanges = <T>(data: T): T => {
  if (!data || typeof data !== 'object') return data
  const sourceConfigs = (data as { source_configs?: unknown }).source_configs
  if (!Array.isArray(sourceConfigs)) return data

  let changed = false
  const nextConfigs = sourceConfigs.map((sourceConfig) => {
    if (!sourceConfig || typeof sourceConfig !== 'object') return sourceConfig
    const range = (sourceConfig as { y_axis_range?: unknown }).y_axis_range as
      | { min?: unknown; max?: unknown }
      | null
      | undefined
    if (
      range &&
      typeof range.min === 'number' &&
      typeof range.max === 'number' &&
      range.max < range.min
    ) {
      changed = true
      return { ...sourceConfig, y_axis_range: null }
    }
    return sourceConfig
  })

  return changed ? { ...data, source_configs: nextConfigs } : data
}
